// Hive level 2 — active once the user has reached 10 bees / 10 words learned.
//
// "Tree, hive and bees" scene: the same tree/branch as level 1, but now a
// classic skep hive hangs from the prominent branch and three bees orbit it,
// with field flowers added at the base. Self-contained React +
// requestAnimationFrame animation.
//
// Coordinate space is the original 1600x900 canvas — the wrapper scales with
// CSS (width/height + aspect-ratio); a measured transform fits the fixed-size
// stage to the wrapper.

import React, { useEffect, useRef, useState } from 'react';

const T_DUR = 13; // seconds per loop
const W = (2 * Math.PI) / T_DUR;

// hive anchor on the prominent branch
const HIVE = { x: 905, y: 470 };

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function leafCluster(cx, cy, blobs, key) {
  return (
    <g key={key}>
      {blobs.map((b, i) => (
        <ellipse key={i} cx={cx + b[0]} cy={cy + b[1]} rx={b[2]} ry={b[3] || b[2]} fill={b[4]} />
      ))}
    </g>
  );
}

function Tree({ t }) {
  const sway1 = Math.sin(W * t) * 2.4;
  const sway2 = Math.sin(W * t + 1.1) * 2.9;
  const sway3 = Math.sin(W * t + 2.3) * 2.0;

  const gDark = '#4e8f43', gMid = '#67ab53', gLite = '#86c46c', gHi = '#a3d585';

  const mainBlobs = [
    [-110, 30, 95, 88, gDark], [100, 40, 92, 86, gDark], [0, 60, 110, 96, gDark],
    [-60, -40, 92, 86, gMid], [70, -30, 96, 90, gMid], [0, -20, 104, 96, gMid],
    [-30, -90, 80, 74, gLite], [55, -80, 76, 70, gLite], [10, -120, 70, 64, gHi],
    [-95, -30, 64, 60, gLite],
  ];
  const endBlobs = [
    [-70, 24, 70, 64, gDark], [64, 30, 66, 60, gDark], [0, 40, 80, 70, gDark],
    [-36, -18, 64, 60, gMid], [44, -22, 62, 58, gMid], [4, -8, 70, 64, gMid],
    [-10, -64, 54, 50, gLite], [40, -52, 48, 46, gHi], [-50, -44, 46, 44, gLite],
  ];
  const midBlobs = [
    [-40, 16, 50, 46, gDark], [40, 20, 48, 44, gDark], [0, 0, 56, 50, gMid],
    [-8, -34, 44, 40, gLite], [28, -26, 38, 36, gHi],
  ];

  return (
    <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0 }}>
      <path
        d="M504 818 C 504 700 512 560 528 470 C 534 436 548 412 566 398
           C 584 412 596 436 602 470 C 618 560 624 700 624 818 Z"
        fill="#8a6a4e"
      />
      <path
        d="M566 398 C 584 412 596 436 602 470 C 618 560 624 700 624 818
           L 584 818 C 582 640 576 520 566 398 Z"
        fill="#735540"
      />
      <path d="M450 821 C 494 792 524 804 566 821 C 612 802 644 794 680 821 Z" fill="#735540" />

      {/* prominent branch */}
      <path
        d="M590 478 C 680 458 800 430 940 392 C 1010 372 1070 356 1118 348
           C 1078 372 1018 392 948 414 C 820 450 700 482 596 512 Z"
        fill="#8a6a4e"
      />
      <path
        d="M590 478 C 680 458 800 430 940 392 C 1010 372 1070 356 1118 348
           C 1086 368 1040 384 986 400 C 850 438 720 472 596 500 Z"
        fill="#7a5a44"
      />
      <path d="M880 410 C 892 380 904 360 928 344 C 916 366 912 388 908 410 Z" fill="#7a5a44" />
      <path d="M720 452 C 728 426 740 408 760 396 C 750 416 748 436 744 456 Z" fill="#7a5a44" />
      <path d="M548 560 C 470 556 410 566 356 590 C 412 580 470 580 540 590 Z" fill="#7a5a44" />

      <g transform={`rotate(${sway1} 560 470)`}>{leafCluster(560, 250, mainBlobs, 'main')}</g>
      <g transform={`rotate(${sway2} 1050 430)`}>{leafCluster(1062, 318, endBlobs, 'end')}</g>
      <g transform={`rotate(${sway3} 380 600)`}>{leafCluster(330, 560, midBlobs, 'mid')}</g>
    </svg>
  );
}

// small classic skep hive hanging from the branch, with a gentle pendulum sway
function Hive({ t }) {
  const swing = Math.sin(W * t + 0.5) * 1.6 + Math.sin(2 * W * t) * 0.5;
  // bands of the skep: [cy, rx, ry]
  const bands = [
    [-30, 20, 14], [-16, 27, 14], [-1, 31, 15], [15, 32, 15], [31, 29, 15], [45, 22, 13],
  ];
  return (
    <svg
      width="1600" height="900" viewBox="0 0 1600 900"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    >
      <g transform={`translate(${HIVE.x} ${HIVE.y}) rotate(${swing} 0 -52)`}>
        {/* short attachment to branch */}
        <path d="M-4 -52 C -6 -40 -6 -36 -4 -30 L 4 -30 C 6 -36 6 -40 4 -52 Z" fill="#7a5a44" />
        {/* hive shadow on itself / soft drop */}
        <ellipse cx="2" cy="46" rx="30" ry="10" fill="rgba(120,80,30,0.18)" />
        {/* skep bands */}
        <g>
          {bands.map((b, i) => (
            <ellipse
              key={i} cx="0" cy={b[0]} rx={b[1]} ry={b[2]}
              fill={i % 2 === 0 ? '#e2a83f' : '#d49a32'} stroke="#b9842a" strokeWidth="1.2"
            />
          ))}
          {/* top cap */}
          <ellipse cx="0" cy="-34" rx="13" ry="9" fill="#e7b24a" stroke="#b9842a" strokeWidth="1.2" />
        </g>
        {/* entrance hole */}
        <ellipse cx="0" cy="20" rx="7" ry="8" fill="#5a3d1e" />
        <ellipse cx="0" cy="17" rx="7" ry="4" fill="#3e2913" />
        {/* tiny landing lip */}
        <path d="M-8 27 Q 0 33 8 27" stroke="#b9842a" strokeWidth="2" fill="none" />
      </g>
    </svg>
  );
}

// parametric flight path; all frequencies are integer multiples of W — seamless loop
function beePos(t, c) {
  const x = c.cx + c.ax * Math.sin(W * t * c.fx + c.px) + c.ax2 * Math.sin(2 * W * t + c.px2);
  const y = c.cy + c.ay * Math.cos(W * t * c.fy + c.py) + c.ay2 * Math.sin(3 * W * t + c.py2);
  const jx = 3.0 * Math.sin(34 * W * t + c.px);
  const jy = 2.6 * Math.cos(40 * W * t + c.py);
  return { x: x + jx, y: y + jy };
}

function Bee({ t, cfg }) {
  const s = cfg.scale || 1;
  const p = beePos(t, cfg);
  const pPrev = beePos(t - 0.06, cfg);
  const pNext = beePos(t + 0.06, cfg);
  const vx = pNext.x - pPrev.x;
  const vy = pNext.y - pPrev.y;
  const faceLeft = vx < 0;
  let pitch = Math.atan2(vy, Math.abs(vx) + 0.001) * (180 / Math.PI);
  pitch = clamp(pitch * 0.5, -22, 22) * (faceLeft ? -1 : 1);

  const flap = 0.35 + 0.65 * Math.abs(Math.sin(t * 52 + cfg.px * 7));

  const trail = [];
  for (let i = 1; i <= 5; i++) {
    const tp = beePos(t - i * 0.045, cfg);
    trail.push(
      <div
        key={i}
        style={{
          position: 'absolute', left: tp.x, top: tp.y,
          width: (6 - i * 0.7) * s, height: (6 - i * 0.7) * s,
          marginLeft: -((3 - i * 0.35) * s), marginTop: -((3 - i * 0.35) * s),
          borderRadius: '50%', background: 'rgba(58,48,38,' + (0.16 - i * 0.026) + ')',
        }}
      />
    );
  }

  const wingFill = 'rgba(214,232,240,0.78)';
  const wingStroke = 'rgba(150,180,196,0.7)';

  return (
    <React.Fragment>
      {trail}
      <div
        style={{
          position: 'absolute', left: p.x, top: p.y,
          transform: `translate(-50%,-50%) rotate(${pitch}deg) scaleX(${faceLeft ? -1 : 1}) scale(${s})`,
          willChange: 'transform, left, top',
        }}
      >
        <svg width="64" height="48" viewBox="-32 -24 64 48" style={{ overflow: 'visible' }}>
          <defs>
            <clipPath id={'beebody' + cfg.id}><ellipse cx="0" cy="0" rx="17" ry="11.5" /></clipPath>
          </defs>
          <g transform={`scale(1,${flap})`} style={{ transformOrigin: '0px -6px' }}>
            <ellipse cx="-4" cy="-15" rx="9" ry="15" fill={wingFill} stroke={wingStroke} strokeWidth="1" transform="rotate(-22 -4 -8)" />
            <ellipse cx="7" cy="-16" rx="8" ry="13" fill={wingFill} stroke={wingStroke} strokeWidth="1" transform="rotate(14 7 -8)" />
          </g>
          <ellipse cx="0" cy="0" rx="17" ry="11.5" fill="#f0bf4b" />
          <g clipPath={'url(#beebody' + cfg.id + ')'}>
            <rect x="-6" y="-12" width="6" height="24" fill="#3a3026" />
            <rect x="4" y="-12" width="6" height="24" fill="#3a3026" />
            <rect x="-15" y="-12" width="4" height="24" fill="#3a3026" />
          </g>
          <ellipse cx="0" cy="0" rx="17" ry="11.5" fill="none" stroke="#caa03a" strokeWidth="1" />
          <circle cx="16" cy="-1" r="8" fill="#3a3026" />
          <circle cx="18.5" cy="-3" r="1.7" fill="#fff" opacity="0.85" />
          <path d="M-17 0 L-25 -2.4 L-25 2.4 Z" fill="#3a3026" />
          <path d="M19 -7 C 23 -13 26 -15 29 -15" stroke="#3a3026" strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <circle cx="29.5" cy="-15" r="1.8" fill="#3a3026" />
        </svg>
      </div>
    </React.Fragment>
  );
}

// three bees, all loosely orbiting the hive
const BEES = [
  { id: 0, cx: 905, cy: 440, ax: 250, fx: 1, ax2: 95, px: 0.0, px2: 1.3, ay: 150, fy: 1, ay2: 64, py: 0.0, py2: 0.6, scale: 1.0 },
  { id: 1, cx: 815, cy: 480, ax: 170, fx: 2, ax2: 70, px: 2.1, px2: 0.4, ay: 120, fy: 1, ay2: 52, py: 1.0, py2: 2.2, scale: 0.82 },
  { id: 2, cx: 1000, cy: 415, ax: 150, fx: 1, ax2: 58, px: 4.1, px2: 3.0, ay: 110, fy: 2, ay2: 46, py: 2.6, py2: 1.1, scale: 0.9 },
];

// a simple field flower that sways from its base
function Flower({ t, x, baseY, h, petal, center, phase, scale }) {
  const sway = Math.sin(W * t + phase) * 3 + Math.sin(2 * W * t + phase) * 1.2;
  const petals = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    petals.push(
      <ellipse
        key={i} cx={Math.cos(a) * 11} cy={Math.sin(a) * 11} rx="8" ry="5.5"
        fill={petal} transform={`rotate(${(a * 180) / Math.PI} 0 0)`}
      />
    );
  }
  return (
    <g transform={`translate(${x} ${baseY}) scale(${scale})`}>
      <g transform={`rotate(${sway} 0 0)`}>
        <path d={`M0 0 Q -6 ${-h * 0.55} 0 ${-h}`} stroke="#5a9c46" strokeWidth="4" fill="none" strokeLinecap="round" />
        <ellipse cx="-7" cy={-h * 0.5} rx="9" ry="4.5" fill="#67ab53" transform={`rotate(-34 -7 ${-h * 0.5})`} />
        <ellipse cx="7" cy={-h * 0.66} rx="8" ry="4" fill="#5a9c46" transform={`rotate(32 7 ${-h * 0.66})`} />
        <g transform={`translate(0 ${-h})`}>
          {petals}
          <circle cx="0" cy="0" r="6.5" fill={center} />
        </g>
      </g>
    </g>
  );
}

function Flowers({ t }) {
  return (
    <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <Flower t={t} x={250} baseY={812} h={70} petal="#f4c64a" center="#9c5a1e" phase={0.0} scale={1.0} />
      <Flower t={t} x={300} baseY={826} h={52} petal="#ef8fb6" center="#f4d34a" phase={1.4} scale={0.85} />
      <Flower t={t} x={1290} baseY={806} h={78} petal="#e98fd0" center="#f4d34a" phase={2.6} scale={1.05} />
      <Flower t={t} x={1230} baseY={824} h={50} petal="#f4c64a" center="#9c5a1e" phase={4.0} scale={0.8} />
    </svg>
  );
}

function Pollen({ t }) {
  const specks = [
    [220, 300, 1.0, 0.0], [1360, 250, 0.7, 1.7], [760, 180, 0.9, 3.0],
    [1180, 520, 0.8, 4.2], [430, 470, 0.6, 2.1], [1460, 430, 0.85, 0.8],
    [150, 560, 0.7, 5.0], [640, 620, 0.6, 1.2],
  ];
  return (
    <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {specks.map((s, i) => {
        const dx = Math.sin(W * t * 1.0 + s[3]) * 26 * s[2];
        const dy = Math.cos(W * t * 1.3 + s[3]) * 20 * s[2];
        const op = 0.25 + 0.25 * (0.5 + 0.5 * Math.sin(W * t * 2 + s[3]));
        return <circle key={i} cx={s[0] + dx} cy={s[1] + dy} r={2.4 * s[2]} fill="#fdf0c0" opacity={op} />;
      })}
    </svg>
  );
}

function Scene({ t }) {
  const camScale = 1.015 + 0.015 * Math.sin(W * t);
  const camX = Math.sin(W * t) * 8;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, #bfe4ef 0%, #d4edec 46%, #eaf3df 72%, #f4efda 100%)',
        }}
      />
      <div
        style={{
          position: 'absolute', left: 1140, top: 90, width: 360, height: 360,
          marginLeft: -180, marginTop: -180, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,246,210,0.95) 0%, rgba(255,246,210,0.35) 38%, rgba(255,246,210,0) 70%)',
        }}
      />
      <div
        style={{
          position: 'absolute', left: 1140, top: 90, width: 96, height: 96,
          marginLeft: -48, marginTop: -48, borderRadius: '50%',
          background: 'radial-gradient(circle, #fff7d6 0%, #ffeeb0 70%, rgba(255,238,176,0) 100%)',
        }}
      />
      <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0 }}>
        <g fill="rgba(255,255,255,0.7)" transform={`translate(${Math.sin(W * t) * 16} 0)`}>
          <ellipse cx="300" cy="160" rx="80" ry="34" />
          <ellipse cx="360" cy="150" rx="64" ry="40" />
          <ellipse cx="240" cy="172" rx="56" ry="30" />
        </g>
        <g fill="rgba(255,255,255,0.55)" transform={`translate(${Math.cos(W * t) * 22} 0)`}>
          <ellipse cx="900" cy="120" rx="70" ry="28" />
          <ellipse cx="950" cy="112" rx="54" ry="34" />
        </g>
      </svg>

      <div
        style={{
          position: 'absolute', inset: 0,
          transform: `translateX(${camX}px) scale(${camScale})`, transformOrigin: '60% 70%',
        }}
      >
        <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0 }}>
          <path d="M0 800 C 300 760 600 770 900 790 C 1200 808 1450 790 1600 770 L1600 900 L0 900 Z" fill="#a9cf86" />
          <path d="M0 840 C 360 812 720 824 1080 838 C 1320 846 1480 838 1600 828 L1600 900 L0 900 Z" fill="#8fbf6e" />
          <ellipse cx="566" cy="816" rx="190" ry="26" fill="rgba(60,90,40,0.16)" />
        </svg>
        <Tree t={t} />
        <Hive t={t} />
        <Flowers t={t} />
        <Pollen t={t} />
        {BEES.map((cfg) => <Bee key={cfg.id} t={t} cfg={cfg} />)}
      </div>

      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          boxShadow: 'inset 0 0 220px rgba(70,90,60,0.22)',
        }}
      />
    </div>
  );
}

// Native canvas size that all child coordinates are authored against.
const STAGE_W = 1600;
const STAGE_H = 900;

export default function HiveLevel2({ className = '', style = {} }) {
  const [t, setT] = useState(0);
  const [scale, setScale] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const wrapRef = useRef(null);
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (reducedMotion.current) return; // leave t at 0, static first frame
    const tick = (now) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = (now - startRef.current) / 1000;
      setT(elapsed % T_DUR);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Keep the fixed-size 1600x900 stage scaled to fit the wrapper's width.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / STAGE_W);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${STAGE_W} / ${STAGE_H}`,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <Scene t={t} />
      </div>
    </div>
  );
}
